import { Button, Col, Icon, Row, Text } from 'components';
import { Api, POLL_STATE, POLL_TYPE, theme } from 'lib';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BarFill, BarTrack, OptionRow, RadioDot, SurfaceCard } from './surfaceStyles';

/**
 * Inline poll card for the Team Channel surface (fan view).
 *
 *  - OPEN & not voted → interactive options + "Vote Now" → POST /polls/submit, then lock
 *    and show points earned + live results.
 *  - voted / CLOSED   → results only (bars + %), the fan's pick highlighted, "Poll Closed".
 *
 * Votes can't be changed. The card renders directly off the surface payload (which already
 * carries fan-scoped `state` / `answered` / `selectedOptionId` / `results`); after a vote we
 * re-fetch /polls/get for fresh results and bubble the merged poll up via onVoted so the
 * parent's surface state stays in sync without a full refetch.
 */
const SurfacePollCard = ({ poll, onVoted }) => {
  const dispatch = useDispatch();
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelectedId(poll?.selectedOptionId || null);
    setSubmitting(false);
  }, [poll?._id, poll?.selectedOptionId]);

  if (!poll) return null;

  const state = poll.state || POLL_STATE.OPEN;
  const answered = poll.answered === true;
  const results = poll.results || null;
  const isGameDay = poll.pollType === POLL_TYPE.GAME_DAY;
  const canVote = state === POLL_STATE.OPEN && !answered;
  const showResults = (answered || state === POLL_STATE.CLOSED) && !!results;

  const breakdownById = new Map((results?.options || []).map((o) => [o.optionId, o]));
  const maxCount = (results?.options || []).reduce((m, o) => Math.max(m, o.count), 0);
  const totalSubmissions = results?.totalSubmissions ?? poll.totalSubmissions ?? 0;

  const submit = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    const res = await Api.post(
      '/polls/submit',
      { pollId: poll._id, optionId: selectedId },
      null,
      { silent: true }
    );

    if (res && res.err && res.status) {
      // 404 deleted, 400 closed / invalid / already-voted.
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
      setSubmitting(false);
      return;
    }

    const earnedPoints = res?.earnedPoints ?? 0;
    const picked = res?.selectedOptionId || selectedId;

    // Pull fresh state + results now that the fan has voted.
    const fresh = await Api.get(`/polls/get/${poll._id}`, null, { silent: true });
    const next = {
      ...poll,
      answered: true,
      selectedOptionId: picked,
      earnedPoints,
      results: fresh?.results || results,
      state: fresh?.state || state,
      totalSubmissions: fresh?.results?.totalSubmissions ?? totalSubmissions
    };
    setSubmitting(false);
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: earnedPoints ? `Vote submitted · +${earnedPoints} pts` : 'Vote submitted.' }
    });
    onVoted && onVoted(next);
  };

  const earned = poll.earnedPoints;

  return (
    <SurfaceCard>
      <Row noFlex center gap="8px" marg="0 0 6px">
        <Text c2 col={theme.GREY_50}>
          {isGameDay ? 'Game-day poll' : 'Poll'}
        </Text>
        {state === POLL_STATE.OPEN && (
          <Text c2 bold col={theme.GREEN}>
            · Open
          </Text>
        )}
        {state === POLL_STATE.CLOSED && (
          <Text c2 col={theme.GREY_60}>
            · Poll Closed
          </Text>
        )}
      </Row>

      <Text h4 marg="0 0 14px">
        {poll.question}
      </Text>

      <Col wid="100%" gap="10px">
        {(poll.options || []).map((o) => {
          const isSelected = poll.selectedOptionId === o._id;
          const b = breakdownById.get(o._id);
          const pct = b?.percentage ?? 0;
          const winner = showResults && maxCount > 0 && (b?.count ?? 0) === maxCount;
          return (
            <OptionRow
              key={o._id}
              type="button"
              interactive={canVote}
              selected={(canVote && selectedId === o._id) || isSelected}
              onClick={canVote ? () => setSelectedId(o._id) : undefined}
              disabled={!canVote}
            >
              <Row between center>
                <Row noFlex center gap="8px" style={{ minWidth: 0 }}>
                  {canVote ? (
                    <RadioDot selected={selectedId === o._id} />
                  ) : (
                    isSelected && <Icon type="check" col={theme.SECONDARY} size={14} />
                  )}
                  <Text col={theme.GREY_80}>{o.text}</Text>
                </Row>
                {showResults && (
                  <Text c2 col={winner ? theme.GREEN : theme.GREY_60} marg="0 0 0 10px">
                    {pct}%
                  </Text>
                )}
              </Row>
              {showResults && (
                <BarTrack>
                  <BarFill pct={pct} winner={winner} />
                </BarTrack>
              )}
            </OptionRow>
          );
        })}
      </Col>

      {/* Footer — voting CTA, post-vote confirmation, or closed note. */}
      {canVote ? (
        <Col wid="100%" marg="16px 0 0" gap="8px">
          <Button
            text={submitting ? 'Submitting…' : 'Vote Now'}
            onClick={submit}
            disabled={!selectedId || submitting}
            bg={theme.SECONDARY}
            wid="100%"
          />
          {poll.rewardPoints > 0 && (
            <Text c2 col={theme.GREY_50} center>
              Earn {poll.rewardPoints} pts for voting. Your vote can&apos;t be changed.
            </Text>
          )}
        </Col>
      ) : answered ? (
        <Col
          wid="100%"
          marg="16px 0 0"
          pad="12px 14px"
          style={{ background: `${theme.GREEN}12`, borderRadius: 10 }}
        >
          <Row center gap="6px">
            <Icon type="check" col={theme.GREEN} size={14} />
            <Text c1 col={theme.GREY_80}>
              Vote submitted{earned ? ` · +${earned} pts` : ''}. Your vote can&apos;t be changed.
            </Text>
          </Row>
        </Col>
      ) : state === POLL_STATE.CLOSED && !results ? (
        <Text c2 col={theme.GREY_50} marg="16px 0 0" center>
          This poll is closed.
        </Text>
      ) : null}

      {showResults && (
        <Text c2 col={theme.GREY_40} marg="12px 0 0">
          {totalSubmissions} total submission{totalSubmissions === 1 ? '' : 's'}
        </Text>
      )}
    </SurfaceCard>
  );
};

export default SurfacePollCard;
